import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, switchMap, of } from 'rxjs';
import {
  AppRoleMember,
  AppRoleMembersResponse,
  GraphUser,
  GraphUserDto,
} from '#models';
import { environment } from '#environments';

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  private _http = inject(HttpClient);

  private readonly _graphBaseUrl = 'https://graph.microsoft.com/v1.0';
  private readonly _apiBaseUrl = environment.apiUrl;

  /**
   * Searches users via the Centaur backend proxy (GET /api/graph/users/search?q=).
   * Routes through the backend to avoid MSAL Graph-scope token acquisition errors
   * when called from component contexts where interactive auth is not possible.
   */
  searchUsers(query: string): Observable<GraphUser[]> {
    const params = new HttpParams().set('q', query);
    return this._http
      .get<GraphUserDto[]>(`${this._apiBaseUrl}/graph/users/search`, { params })
      .pipe(
        map((results) =>
          results.map((r) => ({
            id: r.entraObjectId,
            displayName: r.displayName,
            mail: r.email,
            userPrincipalName: r.email,
            jobTitle: r.title ?? null,
          } satisfies GraphUser)),
        ),
      );
  }

  /**
   * Loads all users assigned to any app role for the given AAD application.
   * Requires Application.Read.All (admin consent) on the tenant.
   */
  getAppMembers(clientId: string): Observable<AppRoleMember[]> {
    return this._http
      .get<{
        value: { id: string }[];
      }>(
        `${this._graphBaseUrl}/servicePrincipals?$filter=appId eq '${clientId}'&$select=id`,
      )
      .pipe(
        switchMap((spResponse) => {
          const spId = spResponse.value[0]?.id;
          if (!spId) return of([]);
          return this._http
            .get<AppRoleMembersResponse>(
              `${this._graphBaseUrl}/servicePrincipals/${spId}/appRoleAssignedTo?$top=999&$select=principalId,principalDisplayName,principalType`,
            )
            .pipe(
              map((r) => r.value.filter((m) => m.principalType === 'User')),
            );
        }),
      );
  }

  getUserById(userId: string): Observable<GraphUser> {
    return this._http.get<GraphUser>(
      `${this._graphBaseUrl}/users/${userId}?$select=id,displayName,mail,userPrincipalName,jobTitle`,
    );
  }
}
